import "@/styles/tabbar.css";

import { useTranslate } from "ra-core";
import {
  useLayoutEffect,
  useRef,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { Link, useLocation, useNavigate } from "react-router";

import { hapticTick } from "../haptic";
import { pillBox, popIcon, slidePill, type PillBox } from "./pillMotion";
import { activeTabIndex, MOBILE_TABS } from "./tabs";

/** Horizontal travel before a press on the bar turns into a scrub. */
const SCRUB_SLOP_PX = 10;

const prefersReducedMotion = () =>
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** The pill's box on `tab`: centred, wrapping its icon and full label. */
const pillOn = (tab: HTMLElement): PillBox => {
  const label = tab.querySelector<HTMLElement>(".tab-label");
  const icon = tab.querySelector("svg");
  const labelPadding = label
    ? Number.parseFloat(getComputedStyle(label).paddingLeft) * 2
    : 0;
  const content = Math.max(
    (label?.scrollWidth ?? 0) - labelPadding,
    icon?.getBoundingClientRect().width ?? 0,
  );
  return pillBox(tab.offsetLeft, tab.offsetWidth, content);
};

/** Puts the pill on tab `index` (hidden for -1), sliding if `animate`. */
const placePill = (
  pill: HTMLElement | null,
  tabs: (HTMLElement | null)[],
  index: number,
  animate: boolean,
) => {
  if (!pill) return;
  const tab = tabs[index];
  pill.dataset.index = String(index);
  if (!tab) {
    pill.setAttribute("data-hidden", "");
    return;
  }
  const wasHidden = pill.hasAttribute("data-hidden");
  const box = pillOn(tab);
  pill.removeAttribute("data-hidden");
  if (animate && !wasHidden && !prefersReducedMotion()) {
    slidePill(pill, box, tab.offsetWidth);
    return;
  }
  for (const animation of pill.getAnimations()) animation.cancel();
  pill.style.width = `${box.width}px`;
  pill.style.translate = `${box.x}px 0`;
};

interface Scrub {
  pointer: number;
  startX: number;
  startY: number;
  active: boolean;
  target: number;
}

/**
 * The tab capsule's tabs (one per MOBILE_TABS entry) and the glass pill behind the active one.
 * The pill is derived from the location (taps, back button, links and the +
 * menu all move it), measured from the DOM, and positioned with `translate`;
 * a route change slides it with a spring and a liquid stretch (./pillMotion),
 * the first render just places it. Dragging along the bar scrubs the pill
 * under the finger; letting go snaps it to the nearest tab and opens it.
 */
export const MobileTabs = () => {
  const translate = useTranslate();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const active = activeTabIndex(pathname);
  const rowRef = useRef<HTMLDivElement>(null);
  const pillRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const placed = useRef<number | null>(null);
  const activeRef = useRef(active);
  const scrub = useRef<Scrub | null>(null);
  const swallowClick = useRef(false);

  activeRef.current = active;

  const place = (index: number, animate: boolean) =>
    placePill(pillRef.current, tabRefs.current, index, animate);

  // Follows the route. Only a change after the first render animates.
  useLayoutEffect(() => {
    const previous = placed.current;
    placed.current = active;
    if (scrub.current?.active) return;
    placePill(pillRef.current, tabRefs.current, active, previous !== null);
    if (previous !== null && previous !== active && !prefersReducedMotion()) {
      popIcon(tabRefs.current[active]?.querySelector("svg"));
    }
  }, [active]);

  // Any width, label length or text size: re-measured on resize, in place.
  useLayoutEffect(() => {
    const row = rowRef.current;
    if (!row || typeof ResizeObserver !== "function") return;
    const observer = new ResizeObserver(() => {
      if (scrub.current?.active) return;
      placePill(pillRef.current, tabRefs.current, activeRef.current, false);
    });
    observer.observe(row);
    return () => observer.disconnect();
  }, []);

  const nearestTab = (pillX: number, width: number) => {
    const center = pillX + width / 2;
    let best = 0;
    let bestDistance = Infinity;
    tabRefs.current.forEach((tab, index) => {
      if (!tab) return;
      const distance = Math.abs(tab.offsetLeft + tab.offsetWidth / 2 - center);
      if (distance < bestDistance) {
        best = index;
        bestDistance = distance;
      }
    });
    return best;
  };

  const markTarget = (index: number | null) => {
    tabRefs.current.forEach((tab, i) =>
      tab?.toggleAttribute("data-scrub-target", i === index),
    );
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    swallowClick.current = false;
    if (!event.isPrimary || event.button !== 0) return;
    scrub.current = {
      pointer: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      active: false,
      target: activeRef.current,
    };
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const current = scrub.current;
    const row = rowRef.current;
    const pill = pillRef.current;
    if (!current || current.pointer !== event.pointerId || !row || !pill) {
      return;
    }
    if (!current.active) {
      const dx = Math.abs(event.clientX - current.startX);
      const dy = Math.abs(event.clientY - current.startY);
      if (dx < SCRUB_SLOP_PX || dx < dy) return;
      current.active = true;
      try {
        row.setPointerCapture(event.pointerId);
      } catch {
        // The pointer can already be gone: scrub anyway.
      }
      for (const animation of pill.getAnimations()) animation.cancel();
      const from = tabRefs.current[activeRef.current] ?? tabRefs.current[0];
      if (from) pill.style.width = `${pillOn(from).width}px`;
      pill.removeAttribute("data-hidden");
      row.setAttribute("data-scrubbing", "");
    }
    const width = pill.offsetWidth;
    const box = row.getBoundingClientRect();
    const x = Math.min(
      Math.max(event.clientX - box.left - width / 2, 0),
      row.clientWidth - width,
    );
    pill.style.translate = `${x}px 0`;
    const target = nearestTab(x, width);
    if (target !== current.target) hapticTick();
    current.target = target;
    markTarget(target);
  };

  const endScrub = (event: ReactPointerEvent<HTMLDivElement>) => {
    const current = scrub.current;
    if (!current || current.pointer !== event.pointerId) return;
    scrub.current = null;
    if (!current.active) return;
    swallowClick.current = true;
    rowRef.current?.removeAttribute("data-scrubbing");
    markTarget(null);
    const tab = MOBILE_TABS[current.target];
    if (event.type === "pointerup" && tab && current.target !== active) {
      navigate(tab.href);
      return;
    }
    // Released on the current tab or cancelled: back to the active tab.
    place(activeRef.current, true);
  };

  // A scrub that ends over a tab must not also follow its link.
  const onClickCapture = (event: ReactMouseEvent) => {
    if (!swallowClick.current) return;
    swallowClick.current = false;
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <div
      ref={rowRef}
      className="tab-row relative flex min-w-0 flex-1 items-stretch gap-0.5"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endScrub}
      onPointerCancel={endScrub}
      onClickCapture={onClickCapture}
    >
      <div
        ref={pillRef}
        aria-hidden
        data-slot="tab-pill"
        data-hidden=""
        className="tab-pill"
      >
        <div className="tab-pill-glass" />
      </div>
      {MOBILE_TABS.map((tab, index) => {
        const isActive = index === active;
        return (
          <Link
            key={tab.href}
            ref={(element) => {
              tabRefs.current[index] = element;
            }}
            to={tab.href}
            aria-current={isActive ? "page" : undefined}
            draggable={false}
            className="plus-tab tab-link relative flex flex-1 rounded-full outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
          >
            <span className="tab-content flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5">
              <tab.Icon className="tab-icon size-5" />
              <span className="tab-label max-w-full truncate px-1 text-[10px] leading-none">
                {translate(tab.label)}
                <span aria-hidden className="tab-label-reserve">
                  {translate(tab.label)}
                </span>
              </span>
            </span>
          </Link>
        );
      })}
    </div>
  );
};
